import * as angular from 'angular';
import * as _ from 'lodash';

import { IApplicationService, IBackupService, IAuthenticationService, ILoggerService } from '../../services';
import { Application as application, KeyValuePair, ComponentDefinition, ApplicationDto, Pattern } from '../../models';
import { applicationCreated, applicationBackuped } from '../../common/events';
import { Roles, Helper } from '../../common';

class ApplicationListController implements angular.IComponentController {
    applications: ApplicationDto[];
    displayed: ApplicationDto[];
    criteria: Pattern<ApplicationDto>[];
    exposedApplication: application;
    isLoading: boolean;
    isSuperAdmin: boolean;
    searchApplication: string;
    selectedApplication: application;
    selectedRowIndex: number;
    sortReverse: boolean;
    sortType: string;
    userApplicationsRoles: KeyValuePair<string, string[]>[] = [];

    static $inject = [
        '$rootScope',
        '$scope',
        '$translate',
        'applicationService',
        'backupService',
        'authenticationService',
        'loggerService'
    ];

    constructor(
        private $rootScope: angular.IRootScopeService,
        private $scope: angular.IScope,
        private $translate: angular.translate.ITranslateService,
        private applicationService: IApplicationService,
        private backupService: IBackupService,
        private authenticationService: IAuthenticationService,
        private loggerService: ILoggerService) {
    }

    $onInit() {
        this.criteria = [
            {
                label: 'Application',
                model: 'a',
                placeholder: 'Application',
                property: 'a',
                isMatch: (d, value) => {
                    return value.test(d.application.name);
                }
            },
            {
                label: 'Administrators',
                model: 'ad',
                placeholder: 'Administrators',
                property: 'ad',
                isMatch: (d, value) => {
                    return d.administratorNames.filter(a => value.test(a)).length > 0;
                }
            },
            {
                label: 'Created by',
                model: 'cr',
                placeholder: 'Created by',
                property: 'cr',
                isMatch: (d, value) => {
                    return value.test(d.creationUserViewModel.userName)
                        || value.test(d.creationUserViewModel.firstName)
                        || value.test(d.creationUserViewModel.lastName)
                        || value.test(d.application.creationUser);
                }
            }
        ];

        this.sortType = 'name'; // set the default sort type
        this.sortReverse = false; // set the default sort order
        this.isSuperAdmin = this.authenticationService.authentication.isSuperAdmin;
        // get the applications list of a user.
        this.readData();
        this.$scope.$on(applicationCreated, this.refresh());
        this.$scope.$watch(() => this.searchApplication, () => this.refreshDisplay());
    }

    private refresh() {
        const self = this;
        return () => {
            self.readData();
        };
    }

    refreshDisplay() {
        const result = [];

        if (this.applications) {
            const regExp = Helper.getRegExpCase(this.searchApplication, true);

            this.applications
                .filter(app => !app.application.name || !this.searchApplication || (regExp && app.application.name.match(regExp)))
                .forEach(app => result.push(app));
        }

        this.displayed = result;
    }

    private readData() {
        this.isLoading = true;
        const userName = this.authenticationService.authentication.userName;

        this.applicationService.getForUser(userName, (data, status) => {
            if (status === 0) {

                if (data.length === 0) {
                    const msg = this.$translate.instant('No application exists.');
                    this.loggerService.logWarning(msg);
                }
                this.applications = data;
                this.applicationService.getRolesForUser(userName)
                    .then((roles) => this.userApplicationsRoles = roles ? roles : []);
            } else {
                this.loggerService.logError('Cannot retrieve the applications list.');
            }
            this.refreshDisplay();
            this.isLoading = false;
        });
    }

    isBackupRole(app: application) {
        const appRoles = this.userApplicationsRoles[app.guid];
        return appRoles && _.intersection(appRoles, Roles.applicationVisibilityRoles).length > 0;
    }

    select(application, index: number) {
        this.selectedApplication = application;
        this.selectedRowIndex = index;
        if (this.isSuperAdmin || this.isBackupRole(application)) {
            application.isBackupRole = this.isBackupRole(application);
            this.exposedApplication = application;
        } else {
            this.exposedApplication = null;
        }
    }

    isSelected(guid) {
        const result = this.selectedApplication &&
            guid === this.selectedApplication.guid;
        return result;
    }

    delete(application) {
        const self = this;
        this.applicationService.delete(application)
            .then(data => {
                self.applications = _.map(data, 'application');
                self.selectedApplication = null;
            }, (reason) => {
                if (reason.message) {
                    self.loggerService.logError(reason.message);
                } else {
                    self.loggerService.logError(reason, false);
                    self.loggerService.logError('Could not delete application.');

                }
                self.selectedApplication = null;
            });
    }

    backup(application) {
        const self = this;
        this.backupService.createBackup(application.guid).then(data => {
            const msg = this.$translate.instant('Backup successfully created.');
            self.loggerService.logSuccess(msg);
            this.$rootScope.$broadcast(applicationBackuped, data);
        }).catch(reason => {
            self.loggerService.logError(reason, false);
            self.loggerService.logError('Could not back up application.');
        });
    }
}

export const wtApplicationList: ComponentDefinition = {
    componentId: 'wtApplicationList',
    controller: ApplicationListController,
    template: require('./applicationList.component.html'),
    controllerAs: 'vm',
    bindings: {
        exposedApplication: '=?',
        applications: '=?'
    }
};
